import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PackagePlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoManager } from "@/components/admin/PhotoManager";
import { mediaUrl } from "@/lib/pricing";
import { Panel, BRL, inputCls, labelCls, btnPrimary, btnGhost, iconBtn } from "./ui";

const EMPTY = { name: "", category: "churrasco", description: "", price: "", unit: "estadia", images: [] };
const CATS = { limpeza: "Limpeza", churrasco: "Kit churrasco / bebidas", outro: "Outro" };
const swCls = "data-[state=checked]:bg-[#0A66C2] data-[state=unchecked]:bg-slate-200 [&>span]:bg-white";

const AddonForm = ({ open, onOpenChange, initial, onSave }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  useEffect(() => { setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [initial, open]);

  const submit = async () => {
    if (f.name.trim().length < 2 || !(Number(f.price) > 0)) return toast.error("Informe o nome e o valor do adicional.");
    setSaving(true);
    try {
      await onSave({ name: f.name.trim(), category: f.category, description: f.description.trim(), price: Number(f.price), unit: f.unit, images: f.images });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-slate-900 border-[#EDE6DA] rounded-3xl max-w-xl max-h-[92vh] overflow-y-auto" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="addon-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{initial ? "Editar adicional" : "Novo adicional"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Nome</label>
            <input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Kit churrasco com bebidas" className={inputCls} data-testid="addon-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Categoria</label>
              <select value={f.category} onChange={(e) => set({ category: e.target.value })} className={inputCls} data-testid="addon-category">
                {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Valor (R$)</label>
              <input type="number" value={f.price} onChange={(e) => set({ price: e.target.value })} className={inputCls} data-testid="addon-price" />
            </div>
            <div>
              <label className={labelCls}>Cobrança</label>
              <select value={f.unit} onChange={(e) => set({ unit: e.target.value })} className={inputCls} data-testid="addon-unit">
                <option value="estadia">Por estadia</option>
                <option value="diaria">Por diária</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>O que está incluso</label>
            <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} className={`${inputCls} h-24 py-2`}
              placeholder="Ex.: carvão, carnes para até 6 pessoas, refrigerantes, cervejas e gelo" data-testid="addon-description" />
          </div>
          <PhotoManager images={f.images} onChange={(images) => set({ images })} />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => onOpenChange(false)} className={btnGhost} data-testid="addon-cancel">Cancelar</button>
            <button onClick={submit} disabled={saving} className={btnPrimary} data-testid="addon-save">{saving ? "Salvando..." : "Salvar adicional"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AddonsAdmin = ({ items, onAdd, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const save = (payload) => (editing ? onUpdate(editing.id, payload) : onAdd(payload));

  return (
    <Panel id="adicionais" title="Adicionais da hospedagem" subtitle="Pacotes de limpeza, kit churrasco com bebidas e outros extras que o hóspede escolhe na reserva."
      testId="addons-admin" action={<button onClick={() => { setEditing(null); setOpen(true); }} className={btnPrimary} data-testid="add-addon-btn"><Plus size={15} /> Novo adicional</button>}>
      <AddonForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">Nenhum adicional cadastrado.</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((a) => (
            <article key={a.id} className={`rounded-3xl border border-slate-200 overflow-hidden ${a.active ? "" : "opacity-60"}`} data-testid={`admin-addon-${a.id}`}>
              <div className="relative h-32 bg-amber-50">
                {a.images?.[0] ? <img src={mediaUrl(a.images[0])} alt={a.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-amber-300"><PackagePlus size={32} /></div>}
                <span className="absolute top-2 left-2 text-[11px] font-bold bg-white/90 text-slate-700 px-2 py-0.5 rounded-full">{CATS[a.category]}</span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-slate-900 truncate">{a.name}</h3>
                  <Switch className={swCls} checked={a.active} onCheckedChange={(v) => onUpdate(a.id, { active: v })} data-testid={`toggle-addon-${a.id}`} />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-extrabold text-[#0A66C2]">{BRL(a.price)}<span className="text-xs text-slate-400 font-medium"> {a.unit === "diaria" ? "por diária" : "por estadia"}</span></span>
                  <div className="flex">
                    <button className={`${iconBtn} hover:text-[#0A66C2]`} onClick={() => { setEditing(a); setOpen(true); }} data-testid={`edit-addon-${a.id}`}><Pencil size={15} /></button>
                    <button className={`${iconBtn} hover:text-red-600`} onClick={() => window.confirm(`Apagar "${a.name}"?`) && onDelete(a.id)} data-testid={`del-addon-${a.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </Panel>
  );
};
